/**
 * Report Screen — AI-generated executive report on the receivables portfolio.
 *
 * Sends aggregated analysis data to Claude, which produces a structured
 * report with narrative commentary, risk assessments, and chart data.
 * Charts are rendered with Highcharts. The report covers:
 *   - Executive Summary with KPI traffic lights
 *   - Eligibility Waterfall
 *   - Concentration & Rating Analysis
 *   - Aging Quality
 *   - Dilution Performance
 *   - Anomalies & Risks
 *   - Strengths
 */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { generateExecutiveReport, loadSavedReport } from "Redux/data/actions";

HighchartsMore(Highcharts);

const fmt = (n) => {
  if (n === undefined || n === null) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(n));
};

// --- Status badge colors ---
const STATUS_COLORS = {
  good: { bg: "#D8F5E8", color: "#1B7A4A", icon: "fa-solid fa-check" },
  neutral: { bg: "#E4E6EC", color: "#3A3D4A", icon: "fa-solid fa-minus" },
  warning: { bg: "#FFF3D6", color: "#8A6A17", icon: "fa-solid fa-exclamation" },
  critical: { bg: "#FDE2E2", color: "#B22525", icon: "fa-solid fa-xmark" },
};

// ================================================================
// Charts
// ================================================================
const WaterfallChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const categories = data.map((d) => d.category);
  const values = data.map((d) => d.value);

  // Build waterfall series
  const seriesData = values.map((v, i) => {
    if (i === 0 || i === values.length - 1 || categories[i] === "AR After X-Aging") {
      return { y: v, color: v >= 0 ? "#79589F" : "#D64545" };
    }
    return { y: v, color: "#D64545" };
  });

  const options = {
    chart: { type: "waterfall", backgroundColor: "transparent", height: 350 },
    title: { text: null },
    xAxis: { categories, labels: { style: { fontSize: "11px", color: "#7A7490" } } },
    yAxis: { title: { text: null }, labels: { formatter: function() { return fmt(this.value); }, style: { color: "#7A7490" } } },
    legend: { enabled: false },
    tooltip: { formatter: function() { return `<b>${this.x}</b><br/>${fmt(this.y)}`; } },
    series: [{ data: seriesData, borderWidth: 0 }],
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const BarChart = ({ data, xKey, yKeys, colors, height }) => {
  if (!data || data.length === 0) return null;

  const options = {
    chart: { type: "bar", backgroundColor: "transparent", height: height || 350 },
    title: { text: null },
    xAxis: { categories: data.map((d) => d[xKey]), labels: { style: { fontSize: "11px", color: "#7A7490" } } },
    yAxis: { title: { text: null }, labels: { formatter: function() { return fmt(this.value); }, style: { color: "#7A7490" } } },
    legend: { enabled: yKeys.length > 1 },
    plotOptions: { bar: { borderRadius: 3, borderWidth: 0 } },
    series: yKeys.map((key, i) => ({
      name: key,
      data: data.map((d) => d[key] || 0),
      color: (colors || ["#79589F", "#3DB887"])[i],
    })),
    tooltip: { formatter: function() { return `<b>${this.x}</b><br/>${this.series.name}: ${fmt(this.y)}`; } },
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const PieChart = ({ data, nameKey, valueKey, height }) => {
  if (!data || data.length === 0) return null;

  const colors = ["#430098", "#79589F", "#A17DC5", "#CEB5E4", "#E8DFF0"];
  const options = {
    chart: { type: "pie", backgroundColor: "transparent", height: height || 300 },
    title: { text: null },
    plotOptions: {
      pie: {
        innerSize: "50%",
        borderWidth: 0,
        dataLabels: {
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
          style: { fontSize: "11px", color: "var(--ev-text)", textOutline: "none" },
          connectorColor: "var(--ev-text-muted)",
        },
      },
    },
    series: [{
      data: data.map((d, i) => ({
        name: d[nameKey],
        y: d[valueKey] || 0,
        color: colors[i % colors.length],
      })),
    }],
    tooltip: { pointFormat: "{point.name}: <b>{point.y:,.0f}</b> ({point.percentage:.1f}%)" },
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const ColumnChart = ({ data, xKey, yKey, color, height }) => {
  if (!data || data.length === 0) return null;

  const options = {
    chart: { type: "column", backgroundColor: "transparent", height: height || 280 },
    title: { text: null },
    xAxis: { categories: data.map((d) => d[xKey]), labels: { style: { fontSize: "10px", color: "#7A7490" } } },
    yAxis: { title: { text: null }, labels: { formatter: function() { return fmt(this.value); }, style: { color: "#7A7490" } } },
    legend: { enabled: false },
    plotOptions: { column: { borderRadius: 3, borderWidth: 0 } },
    series: [{ data: data.map((d) => d[yKey] || 0), color: color || "#79589F" }],
    tooltip: { formatter: function() { return `<b>${this.x}</b><br/>${fmt(this.y)}`; } },
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// ================================================================
// Report Sections
// ================================================================
const Section = ({ title, icon, children }) => (
  <div style={{ marginBottom: "2rem" }}>
    <div style={{
      display: "flex", alignItems: "center", gap: "0.6rem",
      padding: "0.7rem 0", borderBottom: "2px solid var(--ev-border)", marginBottom: "1rem",
    }}>
      <i className={icon} style={{ color: "#79589F", fontSize: "1.15rem" }} />
      <span style={{ fontWeight: 700, fontSize: "1.15rem" }}>{title}</span>
    </div>
    {children}
  </div>
);

const Narrative = ({ text }) => {
  if (!text) return null;
  return (
    <div style={{
      fontSize: "0.95rem", lineHeight: 1.7, color: "var(--ev-text)",
      whiteSpace: "pre-wrap", marginBottom: "1rem",
    }}>
      {text}
    </div>
  );
};

const KPICard = ({ label, value, status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.neutral;
  return (
    <div style={{
      padding: "0.75rem", borderRadius: "var(--ev-radius)",
      border: "1px solid var(--ev-border)", backgroundColor: "var(--ev-white)",
      display: "flex", flexDirection: "column", justifyContent: "space-between",
    }}>
      <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.4rem", lineHeight: 1.3, minHeight: "1.6em" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <span style={{ fontWeight: 800, fontSize: "1.25rem" }}>{value}</span>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: "16px", height: "16px", borderRadius: "50%", flexShrink: 0,
          backgroundColor: s.bg, color: s.color, fontSize: "0.5rem",
        }}>
          <i className={s.icon} />
        </span>
      </div>
    </div>
  );
};

const RiskItem = ({ item }) => {
  const sev = {
    high:     { bg: "rgba(214, 69, 69, 0.12)", border: "rgba(214, 69, 69, 0.3)", badge: "#D64545", badgeText: "#fff" },
    critical: { bg: "rgba(214, 69, 69, 0.12)", border: "rgba(214, 69, 69, 0.3)", badge: "#D64545", badgeText: "#fff" },
    medium:   { bg: "rgba(232, 167, 53, 0.10)", border: "rgba(232, 167, 53, 0.25)", badge: "#E8A735", badgeText: "#fff" },
    warning:  { bg: "rgba(232, 167, 53, 0.10)", border: "rgba(232, 167, 53, 0.25)", badge: "#E8A735", badgeText: "#fff" },
    low:      { bg: "var(--ev-surface)", border: "var(--ev-border)", badge: "var(--ev-text-muted)", badgeText: "var(--ev-white)" },
  };
  const s = sev[item.severity] || sev.low;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: "0.6rem",
      padding: "0.65rem 0.85rem", borderRadius: "var(--ev-radius)",
      border: `1px solid ${s.border}`, backgroundColor: s.bg,
      marginBottom: "0.4rem",
    }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        padding: "0.15em 0.5em", borderRadius: "4px", flexShrink: 0, marginTop: "0.1rem",
        backgroundColor: s.badge, color: s.badgeText,
        fontSize: "0.55rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {item.severity}
      </span>
      <div>
        <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--ev-text)", lineHeight: 1.3 }}>
          {item.title}
        </div>
        <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", marginTop: "0.2rem", lineHeight: 1.5 }}>
          {item.description}
        </div>
      </div>
    </div>
  );
};

const StrengthItem = ({ item }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: "0.6rem",
    padding: "0.7rem 0.9rem", borderRadius: "var(--ev-radius)",
    border: "1px solid rgba(61, 184, 135, 0.25)", backgroundColor: "rgba(61, 184, 135, 0.08)",
    marginBottom: "0.4rem",
  }}>
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0, marginTop: "0.1rem",
      backgroundColor: "#3DB887", color: "#fff", fontSize: "0.65rem",
    }}>
      <i className="fa-solid fa-check" />
    </span>
    <div>
      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--ev-text)", lineHeight: 1.3 }}>
        {item.title}
      </div>
      <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", marginTop: "0.2rem", lineHeight: 1.5 }}>
        {item.description}
      </div>
    </div>
  </div>
);

// ================================================================
// Main Report Component
// ================================================================
const Report = () => {
  const dispatch = useDispatch();
  const [generating, setGenerating] = useState(false);
  const { report } = useSelector((state) => state.data);
  const { hasMaster, savedReport } = useSelector((state) => state.project);

  // Load saved report from project on mount if not already in data store
  useEffect(() => {
    if (savedReport?.data && !report) {
      dispatch(loadSavedReport());
    }
  }, [savedReport, report, dispatch]);

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  const reportData = report?.report;
  const generatedAt = savedReport?.generatedAt;

  return (
    <div>
      {/* Generating modal */}
      {generating && (
        <div className="modal is-active">
          <div className="modal-background" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} />
          <div className="modal-card" style={{ maxWidth: "400px", borderRadius: "var(--ev-radius-lg)", overflow: "hidden" }}>
            <header style={{
              background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
              padding: "1.2rem 1.5rem",
            }}>
              <p style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", margin: 0 }}>
                Generating Report
              </p>
            </header>
            <section style={{ padding: "2rem 1.5rem", backgroundColor: "var(--ev-white)", textAlign: "center" }}>
              <div style={{ marginBottom: "1.2rem" }}>
                <i className="fa-duotone fa-wand-magic-sparkles fa-spin" style={{ fontSize: "2.5rem", color: "#79589F" }} />
              </div>
              <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--ev-text)", marginBottom: "0.5rem" }}>
                Deep analysis in progress
              </div>
              <div style={{ fontSize: "0.82rem", color: "var(--ev-text-muted)", lineHeight: 1.6 }}>
                Everette is analyzing your portfolio data across all calculation modules.
                This may take a moment.
              </div>
              <div style={{
                marginTop: "1.2rem", fontSize: "0.72rem", color: "var(--ev-text-muted)",
                padding: "0.5rem", borderRadius: "var(--ev-radius)",
                backgroundColor: "var(--ev-surface)",
              }}>
                This report is cached and only needs to be regenerated if the underlying data changes.
              </div>
            </section>
          </div>
        </div>
      )}

      <div className="box" style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1.25rem",
        gap: "0.5rem",
      }}>
        <button
          className="button is-medium"
          onClick={() => {
            setGenerating(true);
            dispatch(generateExecutiveReport(() => setGenerating(false)));
          }}
          disabled={generating}
          style={{
            borderRadius: "var(--ev-radius-lg)",
            background: "linear-gradient(135deg, #430098 0%, #6B3FA0 50%, #79589F 100%)",
            color: "#fff",
            border: "none",
            fontWeight: 800,
            fontSize: "1rem",
            padding: "0.75em 2.5em",
            boxShadow: "0 6px 20px rgba(67, 0, 152, 0.3)",
            letterSpacing: "0.03em",
          }}
        >
          <span className="icon" style={{ fontSize: "1.1rem" }}>
            <i className="fa-duotone fa-wand-magic-sparkles" />
          </span>
          <span>{reportData ? "Regenerate Report" : "Generate Report"}</span>
        </button>
        {generatedAt && !generating && (
          <span style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)" }}>
            Generated {new Date(generatedAt).toLocaleString()}
          </span>
        )}
      </div>

      {report?.error && (
        <div className="notification is-danger is-light" style={{ marginTop: "0.75rem" }}>
          {report.error}
        </div>
      )}

      {reportData && (
        <div style={{ marginTop: "1rem" }}>
          {/* Executive Summary */}
          {reportData.executive_summary && (
            <Section title="Executive Summary" icon="fa-duotone fa-gauge-high">
              {reportData.executive_summary.headline && (
                <div style={{
                  fontSize: "1.3rem", fontWeight: 700, color: "var(--ev-purple-dark)",
                  marginBottom: "1rem", lineHeight: 1.4,
                }}>
                  {reportData.executive_summary.headline}
                </div>
              )}
              {reportData.executive_summary.key_metrics && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "0.5rem",
                  marginBottom: "1rem",
                }}>
                  {reportData.executive_summary.key_metrics.map((m, i) => (
                    <KPICard key={i} label={m.label} value={m.value} status={m.status} />
                  ))}
                </div>
              )}
              <Narrative text={reportData.executive_summary.narrative} />
            </Section>
          )}

          {/* Aging Quality */}
          {reportData.aging_quality && (
            <Section title="Portfolio Composition & Aging" icon="fa-duotone fa-timeline">
              {reportData.aging_quality.distribution_chart && (
                <ColumnChart
                  data={reportData.aging_quality.distribution_chart}
                  xKey="bucket"
                  yKey="amount"
                  color="#79589F"
                  height={280}
                />
              )}
              <Narrative text={reportData.aging_quality.narrative} />
            </Section>
          )}

          {/* Roll-Forward Reconciliation */}
          {reportData.rollforward_reconciliation && (
            <Section title="Roll-Forward Reconciliation" icon="fa-duotone fa-code-compare">
              {reportData.rollforward_reconciliation.chart_data && (
                <BarChart
                  data={reportData.rollforward_reconciliation.chart_data}
                  xKey="Reference Date"
                  yKeys={["Sales", "Collection", "Dilution"].filter(
                    (k) => reportData.rollforward_reconciliation.chart_data[0]?.[k] !== undefined
                  )}
                  colors={["#430098", "#3DB887", "#D64545"]}
                  height={300}
                />
              )}
              <Narrative text={reportData.rollforward_reconciliation.narrative} />
            </Section>
          )}

          {/* Concentration Analysis */}
          {reportData.concentration_analysis && (
            <Section title="Buyer Concentration & Credit Quality" icon="fa-duotone fa-chart-pie">
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                <div style={{ flex: "2 1 400px" }}>
                  <BarChart
                    data={reportData.concentration_analysis.top_buyers_chart}
                    xKey="name"
                    yKeys={["gross_ar", "nerb"]}
                    colors={["#CEB5E4", "#430098"]}
                    height={380}
                  />
                </div>
                <div style={{ flex: "1 1 280px" }}>
                  <PieChart
                    data={reportData.concentration_analysis.rating_chart}
                    nameKey="rating"
                    valueKey="ar"
                    height={300}
                  />
                </div>
              </div>
              {/* Top 15 obligor table */}
              {reportData.concentration_analysis.top15_table && (
                <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
                  <table className="table is-fullwidth is-striped is-bordered" style={{ fontSize: "0.75rem" }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Buyer Parent</th>
                        <th style={{ textAlign: "right" }}>Eligible AR</th>
                        <th style={{ textAlign: "right" }}>% of Pool</th>
                        <th>Rating</th>
                        <th style={{ textAlign: "right" }}>Conc. Limit</th>
                        <th style={{ textAlign: "right" }}>NERB</th>
                        <th>Capped</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.concentration_analysis.top15_table.map((row, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td style={{ fontWeight: 600 }}>{row.Buyer_Parent_Name || row["Buyer_Parent_Name"]}</td>
                          <td style={{ textAlign: "right" }}>{fmt(row.ar_after_xaging)}</td>
                          <td style={{ textAlign: "right" }}>{(row.pct_of_nerb || 0).toFixed(1)}%</td>
                          <td>
                            <span className="tag is-small" style={{
                              fontWeight: 700, fontSize: "0.65rem",
                              backgroundColor: row["Standardized Rating"] === "AA" ? "#430098" :
                                row["Standardized Rating"] === "A" ? "#79589F" :
                                row["Standardized Rating"] === "BBB" ? "#E8DFF0" : "#F4F0F8",
                              color: ["AA", "A"].includes(row["Standardized Rating"]) ? "#fff" : "#430098",
                            }}>
                              {row["Standardized Rating"]}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>{(row["Conc. Limit %"] || 0).toFixed(2)}%</td>
                          <td style={{ textAlign: "right" }}>{fmt(row.NERB)}</td>
                          <td>{row.is_capped ? <span style={{ color: "#D64545", fontWeight: 700 }}>Yes</span> : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Narrative text={reportData.concentration_analysis.narrative} />
            </Section>
          )}

          {/* Dilution & Reserve Analysis */}
          {reportData.dilution_performance && (
            <Section title="Dilution & Reserve Analysis" icon="fa-duotone fa-droplet">
              <Narrative text={reportData.dilution_performance.narrative} />
            </Section>
          )}

          {/* Eligibility Waterfall */}
          {reportData.eligibility_waterfall && (
            <Section title="Eligibility Criteria & Exclusions" icon="fa-duotone fa-filter">
              <WaterfallChart data={reportData.eligibility_waterfall.chart_data} />
              <Narrative text={reportData.eligibility_waterfall.narrative} />
            </Section>
          )}

          {/* Anomalies & Risks */}
          {reportData.anomalies_and_risks && reportData.anomalies_and_risks.items && (
            <Section title="Anomalies & Risk Factors" icon="fa-duotone fa-triangle-exclamation">
              {reportData.anomalies_and_risks.items.map((item, i) => (
                <RiskItem key={i} item={item} />
              ))}
            </Section>
          )}

          {/* Strengths */}
          {reportData.strengths && reportData.strengths.items && (
            <Section title="Portfolio Strengths" icon="fa-duotone fa-shield-check">
              {reportData.strengths.items.map((item, i) => (
                <StrengthItem key={i} item={item} />
              ))}
            </Section>
          )}

          {/* Overall Assessment */}
          {reportData.overall_assessment && (
            <Section title="Overall Assessment" icon="fa-duotone fa-gavel">
              <div style={{
                padding: "1.2rem", borderRadius: "var(--ev-radius-lg)",
                background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
                color: "#fff", marginBottom: "1rem",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                  <span style={{
                    fontSize: "1.6rem", fontWeight: 800, letterSpacing: "0.02em",
                  }}>
                    {reportData.overall_assessment.risk_grade}
                  </span>
                  <span style={{
                    fontSize: "0.82rem", opacity: 0.8,
                  }}>
                    ({reportData.overall_assessment.flags_triggered} flag{reportData.overall_assessment.flags_triggered !== 1 ? "s" : ""} triggered)
                  </span>
                </div>
                <div style={{ fontSize: "0.95rem", lineHeight: 1.7, opacity: 0.95 }}>
                  {reportData.overall_assessment.summary}
                </div>
              </div>
              {reportData.overall_assessment.recommendations && (
                <div>
                  <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.6rem" }}>
                    Recommendations
                  </div>
                  {reportData.overall_assessment.recommendations.map((rec, i) => (
                    <div key={i} style={{
                      padding: "0.6rem 0.8rem", borderRadius: "var(--ev-radius)",
                      borderLeft: "3px solid #79589F", backgroundColor: "var(--ev-purple-ghost)",
                      marginBottom: "0.4rem",
                    }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--ev-text)" }}>
                        {i + 1}. {rec.action}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", marginTop: "0.15rem" }}>
                        {rec.rationale}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

export default Report;
